import { getMobileMode } from '@/app/atoms/mobileStateAtom';
import { events, type DirtyObject } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import { intersects } from '@/app/gridGL/helpers/intersects';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { getColumnA1Notation } from '@/app/gridGL/UI/gridHeadings/getA1Notation';
import { GridHeadingsLabels } from '@/app/gridGL/UI/gridHeadings/GridHeadingsLabels';
import { GridHeadingRows } from '@/app/gridGL/UI/gridHeadings/GridHeadingsRows';
import { GRID_HEADING_CHAR_WIDTH, rowHeadingWorldWidth } from '@/app/gridGL/UI/gridHeadings/rowHeadingMetrics';
import { calculateAlphaForGridLines } from '@/app/gridGL/UI/gridUtils';
import { canChangeFrozen } from '@/app/helpers/canChangeFrozen';
import { getCSSVariableTint } from '@/app/helpers/convertColor';
import { isMobileMode } from '@/app/helpers/isMobile';
import type { Size } from '@/app/shared/types/size';
import { colors } from '@/app/theme/colors';
import { CELL_HEIGHT, CELL_WIDTH } from '@/shared/constants/gridConstants';
import { sharedEvents } from '@/shared/sharedEvents';
import type { Point } from 'pixi.js';
import { Container, Graphics, LINE_CAP, Rectangle } from 'pixi.js';

type Selected = 'all' | number[] | undefined;

export type IntersectsHeadings = { column: number | null; row: number | null; corner?: true };

interface HeadingSize {
  width: number;
  height: number;
  unscaledWidth: number;
  unscaledHeight: number;
}

// Constants for headers
export const LABEL_MAXIMUM_WIDTH_PERCENT = 0.9;
export const LABEL_MAXIMUM_HEIGHT_PERCENT = 0.5;
export const GRID_HEADER_FONT_SIZE = 10;
export const ROW_DIGIT_OFFSET = { x: 0, y: -1 };
const GRID_HEADING_RESIZE_TOLERANCE = 3;
const HEADING_CLAMP_EPSILON = 0.001;

// When nothing is frozen, two freeze handles are parked in the top-left corner
// box (like Google Sheets): a vertical bar in the upper-right that freezes
// columns when dragged right, and a horizontal bar in the lower-left that
// freezes rows when dragged down. These fractions describe where the handles
// sit within the corner box (for both drawing and hit-testing).
const CORNER_FREEZE_HANDLE_EDGE = 0.55;
const CORNER_FREEZE_HANDLE_SPAN = 0.6;

// this is the number of digits to use when calculating what horizontal headings are hidden
export const LABEL_DIGITS_TO_CALCULATE_SKIP = 3;

export class GridHeadings extends Container {
  private characterSize: Size = { width: GRID_HEADING_CHAR_WIDTH, height: 8.09523868560791 };
  private headingsGraphics: Graphics;
  private labels: GridHeadingsLabels;
  private corner: Graphics;

  // pinned headings for frozen rows/columns (drawn on top so the scrolling
  // headings pass underneath them)
  private frozenGraphics: Graphics;
  private frozenLabels: GridHeadingsLabels;

  // live preview while dragging a freeze handle: the column/row index to freeze
  // up to, plus the screen position of the boundary (already accounts for
  // whether the boundary lands in the pinned frozen area or the scrolled body)
  freezePreview?: { column?: number; row?: number; screenX?: number; screenY?: number };
  private selectedColumns: Selected;
  private selectedRows: number[] = [];
  private gridLinesColumns: { column: number; x: number; width: number }[] = [];
  private gridLinesRows: { row: number; y: number; height: number }[] = [];

  // Resize boundaries for the pinned frozen headings. `x`/`y` is the drawn world
  // position of the boundary line; `start` is the pinned (shifted) world
  // position of the column/row's near edge so the resize math matches the
  // on-screen frozen strip rather than the body scrolled underneath it.
  private frozenGridLinesColumns: { column: number; x: number; start: number; width: number }[] = [];
  private frozenGridLinesRows: { row: number; y: number; start: number; height: number }[] = [];
  private rowWidth = 0;

  headingSize: HeadingSize = { width: 0, height: 0, unscaledWidth: 0, unscaledHeight: 0 };

  // heading location for hitTest
  private rowRect: Rectangle | undefined;
  private columnRect: Rectangle | undefined;
  private cornerRect: Rectangle | undefined;

  gridHeadingsRows: GridHeadingRows;

  dirty = true;

  constructor() {
    super();
    this.headingsGraphics = this.addChild(new Graphics());
    this.labels = this.addChild(new GridHeadingsLabels());
    this.corner = this.addChild(new Graphics());
    // added after the scrolling headings so frozen headings render on top
    this.frozenGraphics = this.addChild(new Graphics());
    this.frozenLabels = this.addChild(new GridHeadingsLabels());
    this.gridHeadingsRows = new GridHeadingRows();

    events.on('setDirty', this.setDirty);
    events.on('gridSettings', this.onGridSettingsChange);
    sharedEvents.on('changeThemeAppearanceMode', this.onThemeChange);

    // Listen for focus/blur events to update heading colors when grid focus changes
    document.addEventListener('focusin', this.handleFocusChange);
    document.addEventListener('focusout', this.handleFocusChange);
  }

  destroy() {
    events.off('setDirty', this.setDirty);
    events.off('gridSettings', this.onGridSettingsChange);
    sharedEvents.off('changeThemeAppearanceMode', this.onThemeChange);
    document.removeEventListener('focusin', this.handleFocusChange);
    document.removeEventListener('focusout', this.handleFocusChange);
    super.destroy();
  }

  private onThemeChange = () => {
    this.dirty = true;
    pixiApp.setViewportDirty();
  };

  private onGridSettingsChange = () => {
    this.dirty = true;
    pixiApp.setViewportDirty();
  };

  private handleFocusChange = () => {
    // Mark as dirty when focus changes (the update will check if canvas is focused)
    this.dirty = true;
  };

  private setDirty = (dirty: DirtyObject) => {
    if (dirty.headings) {
      this.dirty = true;
    }
  };

  private findIntervalX(i: number): number {
    if (i > 100) return 52;
    if (i > 20) return 26;
    if (i > 10) return 13;
    if (i > 5) return 6;
    return 2;
  }

  private findIntervalY(i: number): number {
    if (i > 250) return 250;
    if (i > 100) return 100;
    if (i > 50) return 50;
    if (i > 25) return 25;
    if (i > 10) return 10;
    if (i > 3) return 5;
    if (i > 2) return 2;
    return 1;
  }

  private getClampedHeadingBounds(bounds: Rectangle, contentClamp: Rectangle): Rectangle {
    const left = Math.max(bounds.left, contentClamp.left);
    const top = Math.max(bounds.top, contentClamp.top);
    const right = Math.min(bounds.right, contentClamp.right);
    const bottom = Math.min(bounds.bottom, contentClamp.bottom);
    const width = Math.max(0, right - left);
    const height = Math.max(0, bottom - top);
    return new Rectangle(left, top, width, height);
  }

  // Fills horizontal bar based on selection.
  private drawHorizontalBar() {
    const viewport = pixiApp.viewport;
    const bounds = viewport.getVisibleBounds();
    const scale = viewport.scaled;
    const cellHeight = CELL_HEIGHT / scale;
    const sheet = sheets.sheet;
    const offsets = sheet.offsets;
    const cursor = sheet.cursor;
    const contentClamp = sheet.getContentClamp();
    const clampedBounds = this.getClampedHeadingBounds(bounds, contentClamp);
    const clampedRight = clampedBounds.left + clampedBounds.width;

    this.headingsGraphics.lineStyle(0);
    this.headingsGraphics.beginFill(colors.headerBackgroundColor);
    this.columnRect = new Rectangle(bounds.left, bounds.top, Math.max(0, clampedRight - bounds.left), cellHeight);
    this.headingsGraphics.drawShape(this.columnRect);
    this.headingsGraphics.endFill();

    // Hide selection highlights in mobile view mode
    if (isMobileMode() && getMobileMode() === 'view') {
      this.selectedColumns = [];
      return;
    }

    // a selected floating chart replaces the cell selection, so the underlying
    // cell ranges are not reflected in the headings (matching Cursor.ts)
    if (cursor.selectedChartId !== undefined) {
      this.selectedColumns = [];
      return;
    }

    const left = Math.max(bounds.left, contentClamp.left);
    const right = Math.min(bounds.right, contentClamp.right);
    if (right <= left) {
      this.selectedColumns = [];
      return;
    }
    const rightForPlacement = Math.max(left, right - HEADING_CLAMP_EPSILON);
    const leftColumn = sheet.getColumnFromScreen(left);
    const rightColumn = sheet.getColumnFromScreen(rightForPlacement);
    // Use light gray for selected headings when grid doesn't have focus, otherwise use accent color
    const selectionColor = gridSettings.isGridFocused() ? content.accentColor : getCSSVariableTint('muted-foreground');
    this.headingsGraphics.beginFill(selectionColor, colors.headerSelectedRowColumnBackgroundColorAlpha);
    const baseColumnRanges = cursor.getSelectedColumnRanges(leftColumn - 1, rightColumn + 1);
    this.selectedColumns = this.expandColumnRangesForMergedCells(baseColumnRanges, leftColumn - 1, rightColumn + 1);
    for (let i = 0; i < this.selectedColumns.length; i += 2) {
      const startPlacement = offsets.getColumnPlacement(this.selectedColumns[i]);
      const start = startPlacement.position;
      let end: number;
      if (this.selectedColumns[i] === this.selectedColumns[i + 1]) {
        end = start + startPlacement.size;
      } else {
        const endPlacement = offsets.getColumnPlacement(this.selectedColumns[i + 1]);
        end = endPlacement.position + endPlacement.size;
      }
      this.headingsGraphics.drawRect(start, viewport.top, end - start, cellHeight);
    }
    this.headingsGraphics.endFill();
  }

  // Adds horizontal labels
  private horizontalLabels() {
    const viewport = pixiApp.viewport;
    const bounds = viewport.getVisibleBounds();
    const scale = viewport.scaled;
    const sheet = sheets.sheet;
    const offsets = sheet.offsets;
    const cellWidth = CELL_WIDTH / scale;
    const cellHeight = CELL_HEIGHT / scale;
    const gridAlpha = calculateAlphaForGridLines(scale);
    const contentClamp = sheet.getContentClamp();
    const clampedLeft = Math.max(bounds.left, contentClamp.left);
    const clampedRight = Math.min(bounds.right, contentClamp.right);
    if (clampedRight <= clampedLeft) {
      return;
    }
    const clampedRightForPlacement = Math.max(clampedLeft, clampedRight - HEADING_CLAMP_EPSILON);
    const start = offsets.getXPlacement(clampedLeft);
    const end = offsets.getXPlacement(clampedRightForPlacement);
    const leftOffset = start.position;
    const rightOffset = end.position + end.size;

    // labelWidth uses the constant for number of digits--this ensures the mod factor doesn't change when panning
    const labelWidth = LABEL_DIGITS_TO_CALCULATE_SKIP * this.characterSize.width;
    let mod = 0;
    if (labelWidth > CELL_WIDTH * scale * LABEL_MAXIMUM_WIDTH_PERCENT) {
      const skipNumbers = Math.ceil((cellWidth * (1 - LABEL_MAXIMUM_WIDTH_PERCENT)) / labelWidth);
      mod = this.findIntervalX(skipNumbers);
    }

    const y = bounds.top + cellHeight / 2.25;
    let column = start.index;
    let currentWidth = 0;
    this.gridLinesColumns = [];

    // keep track of last label to ensure we don't overlap
    let lastLabel: { left: number; right: number; selected: boolean } | undefined = undefined;

    for (let x = leftOffset; x < rightOffset; x += currentWidth) {
      currentWidth = offsets.getColumnWidth(column);
      if (gridAlpha !== 0) {
        if (column > 0) {
          this.headingsGraphics.lineStyle(
            1,
            colors.gridLines,
            colors.headerSelectedRowColumnBackgroundColorAlpha * gridAlpha,
            0.5,
            true
          );
          this.headingsGraphics.moveTo(x, bounds.top);
          this.headingsGraphics.lineTo(x, bounds.top + cellHeight);
        }
        this.gridLinesColumns.push({ column: column - 1, x, width: offsets.getColumnWidth(column - 1) });
      }

      // show selected numbers
      const selected = Array.isArray(this.selectedColumns) ? this.selectedColumns.includes(column) : false;

      // only show the label if selected or mod calculation or first column
      if (
        selected ||
        mod === 0 ||
        (mod === 2 && column % 2 === 1) ||
        (mod !== 2 && column % mod === 0) ||
        column === start.index
      ) {
        const charactersWidth = (this.characterSize.width * column.toString().length) / scale;

        // only show labels that will fit (unless grid lines are hidden)
        if (
          scale < 0.2 || // this fixes a bug where multi letter labels were not showing when zoomed out
          currentWidth > charactersWidth ||
          content.gridLines.alpha < colors.headerSelectedRowColumnBackgroundColorAlpha
        ) {
          // don't show numbers if it overlaps with the selected value (eg, hides B if selected A overlaps it)
          let xPosition = x + currentWidth / 2;
          const left = xPosition - charactersWidth / 2;
          const right = xPosition + charactersWidth / 2;

          // If this label intersects the previous one, decide which to keep.
          // Prioritize selected (boundary) columns over non-selected columns.
          let intersectsLast =
            lastLabel && intersects.lineLineOneDimension(lastLabel.left, lastLabel.right, left, right);
          if (intersectsLast && selected && lastLabel && !lastLabel.selected) {
            this.labels.removeLast();
            intersectsLast = false;
          }

          // Show the label if it doesn't intersect the previous one
          if (!intersectsLast && column > 0) {
            const text = getColumnA1Notation(column);
            this.labels.add({ text, x: xPosition, y });
            lastLabel = { left, right, selected };
          }
        }
      }
      column++;
    }

    // Draw the far-side separator for the last visible heading column.
    if (gridAlpha !== 0 && end.index > 0) {
      this.headingsGraphics.lineStyle(
        1,
        colors.gridLines,
        colors.headerSelectedRowColumnBackgroundColorAlpha * gridAlpha,
        0.5,
        true
      );
      this.headingsGraphics.moveTo(rightOffset, bounds.top);
      this.headingsGraphics.lineTo(rightOffset, bounds.top + cellHeight);
      this.gridLinesColumns.push({ column: end.index, x: rightOffset, width: offsets.getColumnWidth(end.index) });
    }
  }

  private drawHorizontal() {
    this.drawHorizontalBar();
    this.horizontalLabels();
  }

  private calculateRowWidth(bottomNumberLength: number): number {
    return rowHeadingWorldWidth(pixiApp.viewport.scale.x, bottomNumberLength);
  }

  /**
   * Expands column ranges to include all columns within merged cells in the selection.
   * Returns ranges in the format [start1, end1, start2, end2, ...]
   */
  private expandColumnRangesForMergedCells(baseRanges: number[], fromColumn: number, toColumn: number): number[] {
    const sheet = sheets.sheet;
    const cursor = sheet.cursor;

    // Get finite ref range bounds which already includes merged cells
    const finiteRanges = cursor.getFiniteRefRangeBounds();

    // Collect all columns from the finite ranges
    const allColumns = new Set<number>();
    // Track merged cells we've already processed to avoid duplicate work
    const processedMergedCells = new Set<string>();

    // Add columns from base ranges
    for (let i = 0; i < baseRanges.length; i += 2) {
      for (let col = baseRanges[i]; col <= baseRanges[i + 1]; col++) {
        allColumns.add(col);
      }
    }

    // Add columns from finite ranges and check for merged cells
    for (const range of finiteRanges) {
      const startCol = Number(range.start.col.coord);
      const endCol = Number(range.end.col.coord);
      const startRow = Number(range.start.row.coord);
      const endRow = Number(range.end.row.coord);

      // Skip unbounded ranges
      if (startCol === -1 || endCol === -1 || startRow === -1 || endRow === -1) continue;

      const minCol = Math.min(startCol, endCol);
      const maxCol = Math.max(startCol, endCol);
      const minRow = Math.min(startRow, endRow);
      const maxRow = Math.max(startRow, endRow);

      // Add columns from the base selection range
      for (let col = minCol; col <= maxCol; col++) {
        if (col >= fromColumn && col <= toColumn) {
          allColumns.add(col);
        }
      }

      // Get all merged cells in this range
      const rangeRect = new Rectangle(minCol, minRow, maxCol - minCol + 1, maxRow - minRow + 1);
      const mergedCells = sheet.getMergeCellsInRect(rangeRect);

      // Process each merged cell
      for (const mergeRect of mergedCells) {
        // Create a unique key for this merged cell
        const mergeKey = `${mergeRect.min.x},${mergeRect.min.y},${mergeRect.max.x},${mergeRect.max.y}`;

        // Only process if we haven't seen this merged cell before
        if (!processedMergedCells.has(mergeKey)) {
          processedMergedCells.add(mergeKey);

          // Add all columns from this merged cell
          const mergeMinCol = Number(mergeRect.min.x);
          const mergeMaxCol = Number(mergeRect.max.x);

          for (let mergeCol = mergeMinCol; mergeCol <= mergeMaxCol; mergeCol++) {
            if (mergeCol >= fromColumn && mergeCol <= toColumn) {
              allColumns.add(mergeCol);
            }
          }
        }
      }

      // Also check corner cells for merged cells that might partially overlap
      const cornerCells = [
        { col: minCol, row: minRow },
        { col: maxCol, row: minRow },
        { col: minCol, row: maxRow },
        { col: maxCol, row: maxRow },
      ];

      for (const cell of cornerCells) {
        const mergeRect = sheet.getMergeCellRect(cell.col, cell.row);
        if (mergeRect) {
          const mergeKey = `${mergeRect.min.x},${mergeRect.min.y},${mergeRect.max.x},${mergeRect.max.y}`;

          if (!processedMergedCells.has(mergeKey)) {
            processedMergedCells.add(mergeKey);

            const mergeMinCol = Number(mergeRect.min.x);
            const mergeMaxCol = Number(mergeRect.max.x);

            for (let mergeCol = mergeMinCol; mergeCol <= mergeMaxCol; mergeCol++) {
              if (mergeCol >= fromColumn && mergeCol <= toColumn) {
                allColumns.add(mergeCol);
              }
            }
          }
        }
      }
    }

    // Convert set to sorted array
    const sortedColumns = Array.from(allColumns).sort((a, b) => a - b);

    // Convert to ranges format
    if (sortedColumns.length === 0) return [];

    const ranges: number[] = [];
    let rangeStart = sortedColumns[0];
    let rangeEnd = sortedColumns[0];

    for (let i = 1; i < sortedColumns.length; i++) {
      if (sortedColumns[i] === rangeEnd + 1) {
        rangeEnd = sortedColumns[i];
      } else {
        ranges.push(rangeStart, rangeEnd);
        rangeStart = sortedColumns[i];
        rangeEnd = sortedColumns[i];
      }
    }
    ranges.push(rangeStart, rangeEnd);

    return ranges;
  }

  /**
   * Expands row ranges to include all rows within merged cells in the selection.
   * Returns ranges in the format [start1, end1, start2, end2, ...]
   */
  private expandRowRangesForMergedCells(baseRanges: number[], fromRow: number, toRow: number): number[] {
    const sheet = sheets.sheet;
    const cursor = sheet.cursor;

    // Get finite ref range bounds which already includes merged cells
    const finiteRanges = cursor.getFiniteRefRangeBounds();

    // Collect all rows from the finite ranges
    const allRows = new Set<number>();
    // Track merged cells we've already processed to avoid duplicate work
    const processedMergedCells = new Set<string>();

    // Add rows from base ranges
    for (let i = 0; i < baseRanges.length; i += 2) {
      for (let row = baseRanges[i]; row <= baseRanges[i + 1]; row++) {
        allRows.add(row);
      }
    }

    // Add rows from finite ranges and check for merged cells
    for (const range of finiteRanges) {
      const startCol = Number(range.start.col.coord);
      const endCol = Number(range.end.col.coord);
      const startRow = Number(range.start.row.coord);
      const endRow = Number(range.end.row.coord);

      // Skip unbounded ranges
      if (startCol === -1 || endCol === -1 || startRow === -1 || endRow === -1) continue;

      const minCol = Math.min(startCol, endCol);
      const maxCol = Math.max(startCol, endCol);
      const minRow = Math.min(startRow, endRow);
      const maxRow = Math.max(startRow, endRow);

      // Add rows from the base selection range
      for (let row = minRow; row <= maxRow; row++) {
        if (row >= fromRow && row <= toRow) {
          allRows.add(row);
        }
      }

      // Get all merged cells in this range
      const rangeRect = new Rectangle(minCol, minRow, maxCol - minCol + 1, maxRow - minRow + 1);
      const mergedCells = sheet.getMergeCellsInRect(rangeRect);

      // Process each merged cell
      for (const mergeRect of mergedCells) {
        // Create a unique key for this merged cell
        const mergeKey = `${mergeRect.min.x},${mergeRect.min.y},${mergeRect.max.x},${mergeRect.max.y}`;

        // Only process if we haven't seen this merged cell before
        if (!processedMergedCells.has(mergeKey)) {
          processedMergedCells.add(mergeKey);

          // Add all rows from this merged cell
          const mergeMinRow = Number(mergeRect.min.y);
          const mergeMaxRow = Number(mergeRect.max.y);

          for (let mergeRow = mergeMinRow; mergeRow <= mergeMaxRow; mergeRow++) {
            if (mergeRow >= fromRow && mergeRow <= toRow) {
              allRows.add(mergeRow);
            }
          }
        }
      }

      // Also check corner cells for merged cells that might partially overlap
      const cornerCells = [
        { col: minCol, row: minRow },
        { col: maxCol, row: minRow },
        { col: minCol, row: maxRow },
        { col: maxCol, row: maxRow },
      ];

      for (const cell of cornerCells) {
        const mergeRect = sheet.getMergeCellRect(cell.col, cell.row);
        if (mergeRect) {
          const mergeKey = `${mergeRect.min.x},${mergeRect.min.y},${mergeRect.max.x},${mergeRect.max.y}`;

          if (!processedMergedCells.has(mergeKey)) {
            processedMergedCells.add(mergeKey);

            const mergeMinRow = Number(mergeRect.min.y);
            const mergeMaxRow = Number(mergeRect.max.y);

            for (let mergeRow = mergeMinRow; mergeRow <= mergeMaxRow; mergeRow++) {
              if (mergeRow >= fromRow && mergeRow <= toRow) {
                allRows.add(mergeRow);
              }
            }
          }
        }
      }
    }

    // Convert set to sorted array
    const sortedRows = Array.from(allRows).sort((a, b) => a - b);

    // Convert to ranges format
    if (sortedRows.length === 0) return [];

    const ranges: number[] = [];
    let rangeStart = sortedRows[0];
    let rangeEnd = sortedRows[0];

    for (let i = 1; i < sortedRows.length; i++) {
      if (sortedRows[i] === rangeEnd + 1) {
        rangeEnd = sortedRows[i];
      } else {
        ranges.push(rangeStart, rangeEnd);
        rangeStart = sortedRows[i];
        rangeEnd = sortedRows[i];
      }
    }
    ranges.push(rangeStart, rangeEnd);

    return ranges;
  }

  private drawVerticalBar() {
    const viewport = pixiApp.viewport;
    const bounds = viewport.getVisibleBounds();
    const sheet = sheets.sheet;
    const cursor = sheet.cursor;
    const offsets = sheet.offsets;
    const contentClamp = sheet.getContentClamp();
    const clampedBounds = this.getClampedHeadingBounds(bounds, contentClamp);
    const clampedBottom = clampedBounds.top + clampedBounds.height;
    const top = Math.max(bounds.top, contentClamp.top);
    const bottom = Math.min(bounds.bottom, contentClamp.bottom);
    const bottomForPlacement = Math.max(top, bottom - HEADING_CLAMP_EPSILON);

    const end = offsets.getYPlacement(bottomForPlacement);
    const bottomOffset = end.position + end.size;
    const bottomNumberLength = Math.round(bottomOffset / CELL_HEIGHT - 1).toString().length;
    this.rowWidth = this.calculateRowWidth(bottomNumberLength);

    // draw background of vertical bar
    this.headingsGraphics.lineStyle(0);
    this.headingsGraphics.beginFill(colors.headerBackgroundColor);
    // Always start from the topmost part of the viewport (bounds.top) to ensure
    // it extends to the corner, regardless of clamp
    this.rowRect = new Rectangle(bounds.left, bounds.top, this.rowWidth, Math.max(0, clampedBottom - bounds.top));
    this.headingsGraphics.drawShape(this.rowRect);
    this.headingsGraphics.endFill();

    // Hide selection highlights in mobile view mode
    if (isMobileMode() && getMobileMode() === 'view') {
      this.selectedRows = [];
      return;
    }

    // a selected floating chart replaces the cell selection, so the underlying
    // cell ranges are not reflected in the headings (matching Cursor.ts)
    if (cursor.selectedChartId !== undefined) {
      this.selectedRows = [];
      return;
    }

    if (bottom <= top) {
      this.selectedRows = [];
      return;
    }

    // For selection highlighting, use content-clamped viewport extents.
    const topRow = sheet.getRowFromScreen(top);
    const bottomRow = sheet.getRowFromScreen(bottomForPlacement);
    // Use light gray for selected headings when grid doesn't have focus, otherwise use accent color
    const selectionColor = gridSettings.isGridFocused() ? content.accentColor : getCSSVariableTint('muted-foreground');
    this.headingsGraphics.beginFill(selectionColor, colors.headerSelectedRowColumnBackgroundColorAlpha);

    const baseRowRanges = cursor.getSelectedRowRanges(topRow, bottomRow);
    this.selectedRows = this.expandRowRangesForMergedCells(baseRowRanges, topRow, bottomRow);
    for (let i = 0; i < this.selectedRows.length; i += 2) {
      const startPlacement = offsets.getRowPlacement(this.selectedRows[i]);
      const start = startPlacement.position;
      let end: number;
      if (this.selectedRows[i] === this.selectedRows[i + 1]) {
        end = start + startPlacement.size;
      } else {
        const endPlacement = offsets.getRowPlacement(this.selectedRows[i + 1]);
        end = endPlacement.position + endPlacement.size;
      }
      this.headingsGraphics.drawRect(bounds.left, start, this.rowWidth, end - start);
    }
    this.headingsGraphics.endFill();
  }

  private verticalLabels() {
    const viewport = pixiApp.viewport;
    const scale = viewport.scaled;
    const bounds = viewport.getVisibleBounds();
    const sheet = sheets.sheet;
    const offsets = sheet.offsets;
    const cellHeight = CELL_HEIGHT / scale;
    const contentClamp = sheet.getContentClamp();

    const gridAlpha = calculateAlphaForGridLines(scale);
    const clampedTop = Math.max(bounds.top, contentClamp.top);
    const clampedBottom = Math.min(bounds.bottom, contentClamp.bottom);
    if (clampedBottom <= clampedTop) {
      return;
    }
    const clampedBottomForPlacement = Math.max(clampedTop, clampedBottom - HEADING_CLAMP_EPSILON);
    const start = offsets.getYPlacement(clampedTop);
    const end = offsets.getYPlacement(clampedBottomForPlacement);
    const topOffset = start.position;
    const bottomOffset = end.position + end.size;

    // labelWidth uses the constant for number of digits--this ensures the mod factor doesn't change when panning
    let mod = 0;
    if (this.characterSize.height > CELL_HEIGHT * scale * LABEL_MAXIMUM_HEIGHT_PERCENT) {
      const skipNumbers = Math.ceil((cellHeight * (1 - LABEL_MAXIMUM_HEIGHT_PERCENT)) / this.characterSize.height);
      mod = this.findIntervalY(skipNumbers);
    }

    const x = bounds.left + this.rowWidth / 2;
    let row = start.index;
    let currentHeight = 0;
    this.gridLinesRows = [];

    // keep track of last label to ensure we don't overlap
    let lastLabel: { top: number; bottom: number; selected: boolean } | undefined = undefined;

    const halfCharacterHeight = this.characterSize.height / scale;

    // Only include boundary rows (first and last of each range) for label prioritization.
    // This prevents too many labels from being shown when a large range is selected.
    const selectedRows: number[] = [];
    if (this.selectedRows) {
      for (let i = 0; i < this.selectedRows.length; i += 2) {
        const rangeStart = this.selectedRows[i];
        const rangeEnd = this.selectedRows[i + 1];
        selectedRows.push(rangeStart);
        if (rangeEnd !== rangeStart) {
          selectedRows.push(rangeEnd);
        }
      }
    }

    for (let y = topOffset; y < bottomOffset; y += currentHeight) {
      currentHeight = offsets.getRowHeight(row);
      if (gridAlpha !== 0) {
        if (row > 0) {
          this.headingsGraphics.lineStyle({
            width: 1,
            color: colors.gridLines,
            alpha: colors.headerSelectedRowColumnBackgroundColorAlpha * gridAlpha,
            alignment: 0.5,
            native: true,
          });
          this.headingsGraphics.moveTo(bounds.left, y);
          this.headingsGraphics.lineTo(bounds.left + this.rowWidth, y);
        }
        this.gridLinesRows.push({ row: row - 1, y, height: offsets.getRowHeight(row - 1) });
      }

      // show selected numbers
      const selected = selectedRows.includes(row);

      // only show the label if selected or mod calculation or first row
      if (
        selected ||
        mod === 0 ||
        (mod === 2 && row % 2 === 1) ||
        (mod !== 2 && row % mod === 0) ||
        row === start.index
      ) {
        // Skip hidden rows. The overlap detection below handles spacing for
        // short rows, mirroring how the x-axis keeps some labels visible at
        // every zoom level instead of dropping them entirely when the row
        // height is smaller than the character height.
        if (currentHeight === 0) {
          row++;
          continue;
        }

        let yPosition = y + currentHeight / 2;
        const top = yPosition - halfCharacterHeight / 2;
        const bottom = yPosition + halfCharacterHeight / 2;

        // If this label intersects the previous one, decide which to keep.
        // Prioritize selected (boundary) rows over non-selected rows.
        let intersectsLast = lastLabel && intersects.lineLineOneDimension(lastLabel.top, lastLabel.bottom, top, bottom);
        if (intersectsLast && selected && lastLabel && !lastLabel.selected) {
          this.labels.removeLast();
          intersectsLast = false;
        }

        // Show the label if it doesn't intersect the previous one
        if (!intersectsLast && row > 0) {
          const text = row.toString();
          this.labels.add({ text, x: x + ROW_DIGIT_OFFSET.x, y: yPosition + ROW_DIGIT_OFFSET.y });
          lastLabel = { top, bottom, selected };
        }
      }
      row++;
    }

    // Draw the far-side separator for the last visible heading row.
    if (gridAlpha !== 0 && end.index > 0) {
      this.headingsGraphics.lineStyle({
        width: 1,
        color: colors.gridLines,
        alpha: colors.headerSelectedRowColumnBackgroundColorAlpha * gridAlpha,
        alignment: 0.5,
        native: true,
      });
      this.headingsGraphics.moveTo(bounds.left, bottomOffset);
      this.headingsGraphics.lineTo(bounds.left + this.rowWidth, bottomOffset);
      this.gridLinesRows.push({ row: end.index, y: bottomOffset, height: offsets.getRowHeight(end.index) });
    }
  }

  private drawVertical() {
    this.drawVerticalBar();
    this.verticalLabels();
  }

  private drawCorner() {
    const { viewport } = pixiApp;
    const bounds = viewport.getVisibleBounds();
    const cellHeight = CELL_HEIGHT / viewport.scale.x;
    this.corner.clear();
    this.corner.beginFill(colors.headerCornerBackgroundColor);
    // Always position at the top-left of viewport bounds to ensure connection with headers
    this.cornerRect = new Rectangle(bounds.left, bounds.top, this.rowWidth, cellHeight);
    this.corner.drawShape(this.cornerRect);
    this.corner.endFill();
    this.corner.lineStyle(1, colors.gridLines, colors.headerSelectedRowColumnBackgroundColorAlpha, 0, true);
    this.corner.moveTo(bounds.left + this.rowWidth, bounds.top);
    this.corner.lineTo(bounds.left + this.rowWidth, bounds.top + cellHeight);
    this.corner.lineTo(bounds.left, bounds.top + cellHeight);

    this.drawCornerFreezeHandles(bounds.left, bounds.top, this.rowWidth, cellHeight);
  }

  // Draws the parked freeze handles in the corner box. The handle for an axis is
  // shown only when that axis is not yet frozen, so a user can still freeze the
  // remaining axis after freezing the first one. The bars are kept inside the
  // corner so the rest of the box still triggers select-all/zoom-to-fit. Drawn
  // in world coords but sized to stay a constant ~2px on screen regardless of
  // zoom.
  private drawCornerFreezeHandles(left: number, top: number, width: number, height: number) {
    const sheet = sheets.sheet;
    if (sheet.frozenColumns !== 0 && sheet.frozenRows !== 0) return;
    if (!canChangeFrozen()) return;

    const handleColor = getCSSVariableTint('muted-foreground');
    const lineWidth = 2 / pixiApp.viewport.scale.x;
    this.corner.lineStyle({
      width: lineWidth,
      color: handleColor,
      alpha: 0.8,
      alignment: 0.5,
      native: false,
      cap: LINE_CAP.ROUND,
    });

    // column handle: vertical bar in the upper-right (drag right to freeze columns)
    if (sheet.frozenColumns === 0) {
      const columnX = left + width * 0.78;
      this.corner.moveTo(columnX, top + height * 0.1);
      this.corner.lineTo(columnX, top + height * 0.6);
    }

    // row handle: horizontal bar in the lower-left (drag down to freeze rows)
    if (sheet.frozenRows === 0) {
      const rowY = top + height * 0.78;
      this.corner.moveTo(left + width * 0.1, rowY);
      this.corner.lineTo(left + width * 0.6, rowY);
    }
  }

  // draws the lines under and to the right of the headings
  private drawHeadingLines() {
    const { viewport } = pixiApp;
    const cellHeight = CELL_HEIGHT / viewport.scale.x;
    const bounds = viewport.getVisibleBounds();
    const contentClamp = sheets.sheet.getContentClamp();
    const clampedBounds = this.getClampedHeadingBounds(bounds, contentClamp);
    const lineBottom = clampedBounds.top + clampedBounds.height;
    const lineRight = clampedBounds.left + clampedBounds.width;
    this.headingsGraphics.lineStyle(1, colors.gridLines, colors.headerSelectedRowColumnBackgroundColorAlpha, 0.5, true);

    // draw the left line to the right of the headings
    // Start from bounds.top to ensure it connects with the corner
    this.headingsGraphics.moveTo(bounds.left + this.rowWidth, bounds.top);
    this.headingsGraphics.lineTo(bounds.left + this.rowWidth, lineBottom);

    // draw the top line under the headings
    // Start from bounds.left to ensure it connects with the corner
    this.headingsGraphics.moveTo(bounds.left, bounds.top + cellHeight);
    this.headingsGraphics.lineTo(lineRight, bounds.top + cellHeight);
  }

  private valueInRanges(ranges: number[], value: number): boolean {
    for (let i = 0; i + 1 < ranges.length; i += 2) {
      if (value >= ranges[i] && value <= ranges[i + 1]) return true;
    }
    return false;
  }

  // Draws the frozen row/column headings pinned in place (on top of the
  // scrolling headings). Only draws an axis once the viewport has scrolled past
  // the freeze boundary on that axis; before then the scrolling headings are
  // already in the correct place.
  private drawFrozen() {
    this.frozenGraphics.clear();
    this.frozenLabels.clear();
    this.frozenGridLinesColumns = [];
    this.frozenGridLinesRows = [];

    const sheet = sheets.sheet;
    if (sheet.frozenColumns === 0 && sheet.frozenRows === 0) return;

    const viewport = pixiApp.viewport;
    const scale = viewport.scaled;
    const bounds = viewport.getVisibleBounds();
    const offsets = sheet.offsets;
    const cursor = sheet.cursor;
    const cellHeight = CELL_HEIGHT / scale;
    const rowWidth = this.rowWidth;
    const boundary = sheet.getFrozenBoundary();
    const gridAlpha = calculateAlphaForGridLines(scale);
    const lineAlpha = colors.headerSelectedRowColumnBackgroundColorAlpha * gridAlpha;
    const selectionColor = gridSettings.isGridFocused() ? content.accentColor : getCSSVariableTint('muted-foreground');
    const selectionAlpha = colors.headerSelectedRowColumnBackgroundColorAlpha;

    // shifts that pin a frozen world coordinate to the heading edge
    const colShift = bounds.left + rowWidth;
    const rowShift = bounds.top + cellHeight;

    // frozen column headings (pinned horizontally) — only when scrolled right
    if (sheet.frozenColumns > 0 && colShift > 0.01) {
      const top = bounds.top;
      const regionLeft = colShift;
      const regionRight = colShift + boundary.x;

      this.frozenGraphics.beginFill(colors.headerBackgroundColor);
      this.frozenGraphics.drawRect(regionLeft, top, regionRight - regionLeft, cellHeight);
      this.frozenGraphics.endFill();

      // Selection is computed for the frozen range directly (the visible-range
      // selection cached during drawHorizontal excludes scrolled-away frozen
      // columns). A selected floating chart suppresses the cell selection.
      const selectedColumns =
        cursor.selectedChartId !== undefined
          ? []
          : this.expandColumnRangesForMergedCells(
              cursor.getSelectedColumnRanges(1, sheet.frozenColumns),
              1,
              sheet.frozenColumns
            );
      this.frozenGraphics.beginFill(selectionColor, selectionAlpha);
      for (let column = 1; column <= sheet.frozenColumns; column++) {
        if (this.valueInRanges(selectedColumns, column)) {
          const placement = offsets.getColumnPlacement(column);
          this.frozenGraphics.drawRect(placement.position + colShift, top, placement.size, cellHeight);
        }
      }
      this.frozenGraphics.endFill();

      const labelY = top + cellHeight / 2.25;
      if (gridAlpha !== 0) {
        this.frozenGraphics.lineStyle(1, colors.gridLines, lineAlpha, 0.5, true);
      }

      // Cull overlapping labels using the same skip-interval and intersection
      // logic as the scrolling column headings (horizontalLabels), so the
      // pinned numbers stay readable when zoomed out.
      const cellWidth = CELL_WIDTH / scale;
      const labelWidth = LABEL_DIGITS_TO_CALCULATE_SKIP * this.characterSize.width;
      let mod = 0;
      if (labelWidth > CELL_WIDTH * scale * LABEL_MAXIMUM_WIDTH_PERCENT) {
        const skipNumbers = Math.ceil((cellWidth * (1 - LABEL_MAXIMUM_WIDTH_PERCENT)) / labelWidth);
        mod = this.findIntervalX(skipNumbers);
      }
      let lastColumnLabel: { left: number; right: number; selected: boolean } | undefined = undefined;

      // track the previous frozen column so each boundary line resizes the
      // column to its left (matching the body heading resize convention)
      let prevColumnLeft: number | undefined;
      let prevColumnWidth = 0;

      for (let column = 1; column <= sheet.frozenColumns; column++) {
        const placement = offsets.getColumnPlacement(column);
        const x = placement.position + colShift;
        const currentWidth = placement.size;
        if (gridAlpha !== 0 && column > 1) {
          this.frozenGraphics.moveTo(x, top);
          this.frozenGraphics.lineTo(x, top + cellHeight);
        }
        if (prevColumnLeft !== undefined) {
          this.frozenGridLinesColumns.push({ column: column - 1, x, start: prevColumnLeft, width: prevColumnWidth });
        }
        prevColumnLeft = x;
        prevColumnWidth = currentWidth;

        const selected = this.valueInRanges(selectedColumns, column);
        if (
          selected ||
          mod === 0 ||
          (mod === 2 && column % 2 === 1) ||
          (mod !== 2 && column % mod === 0) ||
          column === 1
        ) {
          const charactersWidth = (this.characterSize.width * column.toString().length) / scale;
          if (
            scale < 0.2 ||
            currentWidth > charactersWidth ||
            content.gridLines.alpha < colors.headerSelectedRowColumnBackgroundColorAlpha
          ) {
            const xPosition = x + currentWidth / 2;
            const left = xPosition - charactersWidth / 2;
            const right = xPosition + charactersWidth / 2;

            let intersectsLast =
              lastColumnLabel &&
              intersects.lineLineOneDimension(lastColumnLabel.left, lastColumnLabel.right, left, right);
            if (intersectsLast && selected && lastColumnLabel && !lastColumnLabel.selected) {
              this.frozenLabels.removeLast();
              intersectsLast = false;
            }

            if (!intersectsLast) {
              this.frozenLabels.add({ text: getColumnA1Notation(column), x: xPosition, y: labelY });
              lastColumnLabel = { left, right, selected };
            }
          }
        }
      }
      if (prevColumnLeft !== undefined) {
        this.frozenGridLinesColumns.push({
          column: sheet.frozenColumns,
          x: regionRight,
          start: prevColumnLeft,
          width: prevColumnWidth,
        });
      }

      // separator at the freeze boundary + heading line under the strip
      this.frozenGraphics.lineStyle(1, colors.gridLines, colors.headerSelectedRowColumnBackgroundColorAlpha, 0.5, true);
      this.frozenGraphics.moveTo(regionRight, top);
      this.frozenGraphics.lineTo(regionRight, top + cellHeight);
      this.frozenGraphics.moveTo(regionLeft, top + cellHeight);
      this.frozenGraphics.lineTo(regionRight, top + cellHeight);
    }

    // frozen row headings (pinned vertically) — only when scrolled down
    if (sheet.frozenRows > 0 && rowShift > 0.01) {
      const left = bounds.left;
      const regionTop = rowShift;
      const regionBottom = rowShift + boundary.y;

      this.frozenGraphics.beginFill(colors.headerBackgroundColor);
      this.frozenGraphics.drawRect(left, regionTop, rowWidth, regionBottom - regionTop);
      this.frozenGraphics.endFill();

      // A selected floating chart suppresses the cell selection.
      const selectedRows =
        cursor.selectedChartId !== undefined
          ? []
          : this.expandRowRangesForMergedCells(cursor.getSelectedRowRanges(1, sheet.frozenRows), 1, sheet.frozenRows);
      this.frozenGraphics.beginFill(selectionColor, selectionAlpha);
      for (let row = 1; row <= sheet.frozenRows; row++) {
        if (this.valueInRanges(selectedRows, row)) {
          const placement = offsets.getRowPlacement(row);
          this.frozenGraphics.drawRect(left, placement.position + rowShift, rowWidth, placement.size);
        }
      }
      this.frozenGraphics.endFill();

      const labelX = left + rowWidth / 2;
      if (gridAlpha !== 0) {
        this.frozenGraphics.lineStyle({
          width: 1,
          color: colors.gridLines,
          alpha: lineAlpha,
          alignment: 0.5,
          native: true,
        });
      }

      // Cull overlapping labels using the same skip-interval and intersection
      // logic as the scrolling row headings (verticalLabels), so the pinned
      // numbers stay readable when zoomed out.
      let mod = 0;
      if (this.characterSize.height > CELL_HEIGHT * scale * LABEL_MAXIMUM_HEIGHT_PERCENT) {
        const skipNumbers = Math.ceil((cellHeight * (1 - LABEL_MAXIMUM_HEIGHT_PERCENT)) / this.characterSize.height);
        mod = this.findIntervalY(skipNumbers);
      }
      const halfCharacterHeight = this.characterSize.height / scale;
      let lastRowLabel: { top: number; bottom: number; selected: boolean } | undefined = undefined;

      // track the previous frozen row so each boundary line resizes the row
      // above it (matching the body heading resize convention)
      let prevRowTop: number | undefined;
      let prevRowHeight = 0;

      for (let row = 1; row <= sheet.frozenRows; row++) {
        const placement = offsets.getRowPlacement(row);
        const y = placement.position + rowShift;
        const currentHeight = placement.size;
        if (gridAlpha !== 0 && row > 1) {
          this.frozenGraphics.moveTo(left, y);
          this.frozenGraphics.lineTo(left + rowWidth, y);
        }
        if (prevRowTop !== undefined) {
          this.frozenGridLinesRows.push({ row: row - 1, y, start: prevRowTop, height: prevRowHeight });
        }
        prevRowTop = y;
        prevRowHeight = currentHeight;

        const selected = this.valueInRanges(selectedRows, row);
        if (selected || mod === 0 || (mod === 2 && row % 2 === 1) || (mod !== 2 && row % mod === 0) || row === 1) {
          if (currentHeight === 0) continue;

          const yPosition = y + currentHeight / 2;
          const labelTop = yPosition - halfCharacterHeight / 2;
          const labelBottom = yPosition + halfCharacterHeight / 2;

          let intersectsLast =
            lastRowLabel &&
            intersects.lineLineOneDimension(lastRowLabel.top, lastRowLabel.bottom, labelTop, labelBottom);
          if (intersectsLast && selected && lastRowLabel && !lastRowLabel.selected) {
            this.frozenLabels.removeLast();
            intersectsLast = false;
          }

          if (!intersectsLast) {
            this.frozenLabels.add({
              text: row.toString(),
              x: labelX + ROW_DIGIT_OFFSET.x,
              y: yPosition + ROW_DIGIT_OFFSET.y,
            });
            lastRowLabel = { top: labelTop, bottom: labelBottom, selected };
          }
        }
      }
      if (prevRowTop !== undefined) {
        this.frozenGridLinesRows.push({
          row: sheet.frozenRows,
          y: regionBottom,
          start: prevRowTop,
          height: prevRowHeight,
        });
      }

      // separator at the freeze boundary + heading line to the right of the strip
      this.frozenGraphics.lineStyle(1, colors.gridLines, colors.headerSelectedRowColumnBackgroundColorAlpha, 0.5, true);
      this.frozenGraphics.moveTo(left, regionBottom);
      this.frozenGraphics.lineTo(left + rowWidth, regionBottom);
      this.frozenGraphics.moveTo(left + rowWidth, regionTop);
      this.frozenGraphics.lineTo(left + rowWidth, regionBottom);
    }

    this.frozenLabels.update();
  }

  // Screen-space hit test for the draggable freeze divider lines. The freeze
  // divider only exists when an axis is frozen, so this is for *adjusting* an
  // existing freeze (initiating is done via the menus). The hit zone is the
  // divider line in the grid body — the heading itself is reserved for
  // column/row resize.
  intersectsFreezeHandle(global: Point): { column?: true; row?: true } | undefined {
    if (!gridSettings.showHeadings()) return;
    const sheet = sheets.sheet;
    const scale = pixiApp.viewport.scale.x;
    const headingWidth = this.headingSize.width;
    const headingHeight = this.headingSize.height;

    // The corner box hosts a parked handle for each axis that is not yet frozen
    // (see drawCornerFreezeHandles): the right/upper region freezes columns, the
    // bottom/left region freezes rows. This lets the user freeze the remaining
    // axis even after the first one is frozen.
    if (
      (sheet.frozenColumns === 0 || sheet.frozenRows === 0) &&
      global.x >= 0 &&
      global.x <= headingWidth &&
      global.y >= 0 &&
      global.y <= headingHeight
    ) {
      if (
        sheet.frozenColumns === 0 &&
        global.x >= headingWidth * CORNER_FREEZE_HANDLE_EDGE &&
        global.y <= headingHeight * CORNER_FREEZE_HANDLE_SPAN
      ) {
        return { column: true };
      }
      if (
        sheet.frozenRows === 0 &&
        global.y >= headingHeight * CORNER_FREEZE_HANDLE_EDGE &&
        global.x <= headingWidth * CORNER_FREEZE_HANDLE_SPAN
      ) {
        return { row: true };
      }
      return;
    }

    const boundary = sheet.getFrozenBoundary();
    // Match the re-pinned origin used by the frozen-pane render so the draggable
    // hit zone stays on the drawn divider when the origin drifts past the heading
    // edge (e.g. while zooming out).
    const originX = Math.max(headingWidth, pixiApp.viewport.x);
    const originY = Math.max(headingHeight, pixiApp.viewport.y);
    const freezeScreenX = originX + boundary.x * scale;
    const freezeScreenY = originY + boundary.y * scale;
    const tolerance = 4;

    // column divider: vertical line in the grid body below the column heading
    if (
      sheet.frozenColumns > 0 &&
      global.y >= headingHeight &&
      global.x >= headingWidth &&
      Math.abs(global.x - freezeScreenX) <= tolerance
    ) {
      return { column: true };
    }
    // row divider: horizontal line in the grid body right of the row heading
    if (
      sheet.frozenRows > 0 &&
      global.x >= headingWidth &&
      global.y >= headingHeight &&
      Math.abs(global.y - freezeScreenY) <= tolerance
    ) {
      return { row: true };
    }
    return;
  }

  update = (viewportDirty: boolean) => {
    if (!this.dirty && !viewportDirty) return;

    this.dirty = false;
    this.labels.clear();
    this.headingsGraphics.clear();
    this.gridHeadingsRows.labels.clear();
    this.gridHeadingsRows.headingsGraphics.clear();

    if (!gridSettings.showHeadings()) {
      this.visible = false;
      this.rowRect = undefined;
      this.columnRect = undefined;
      this.frozenGraphics.clear();
      this.frozenLabels.clear();
      this.headingSize = { width: 0, height: 0, unscaledWidth: 0, unscaledHeight: 0 };
      events.emit('headingSize', this.headingSize.width, this.headingSize.height);
      pixiApp.setViewportDirty();
      return;
    }

    this.visible = true;
    this.drawVertical();
    this.drawHorizontal();
    this.drawHeadingLines();
    this.labels.update();
    this.drawCorner();
    this.drawFrozen();
    this.headingSize = {
      width: this.rowWidth * pixiApp.viewport.scale.x,
      height: CELL_HEIGHT,
      unscaledWidth: this.rowWidth,
      unscaledHeight: CELL_HEIGHT / pixiApp.viewport.scale.y,
    };
    events.emit('headingSize', this.headingSize.width, this.headingSize.height);
  };

  // The pinned frozen headings are drawn on top of the scrolling headings (see
  // drawFrozen), so a pointer over a frozen strip must resolve to the frozen
  // column/row rather than whatever body column/row has scrolled underneath it.
  // Returns the axis plus the pinned shift that maps the strip's world
  // coordinate back to the body coordinate used by the offset lookups.
  private frozenHeadingAt(world: Point): { axis: 'column' | 'row'; shift: number } | undefined {
    if (!this.columnRect || !this.rowRect) return;
    const sheet = sheets.sheet;
    if (sheet.frozenColumns === 0 && sheet.frozenRows === 0) return;

    const viewport = pixiApp.viewport;
    const scale = viewport.scaled;
    const bounds = viewport.getVisibleBounds();
    const cellHeight = CELL_HEIGHT / scale;
    const boundary = sheet.getFrozenBoundary();
    const colShift = bounds.left + this.rowWidth;
    const rowShift = bounds.top + cellHeight;

    if (
      sheet.frozenColumns > 0 &&
      intersects.rectanglePoint(this.columnRect, world) &&
      world.x >= colShift &&
      world.x <= colShift + boundary.x
    ) {
      return { axis: 'column', shift: colShift };
    }
    if (
      sheet.frozenRows > 0 &&
      intersects.rectanglePoint(this.rowRect, world) &&
      world.y >= rowShift &&
      world.y <= rowShift + boundary.y
    ) {
      return { axis: 'row', shift: rowShift };
    }
  }

  // whether the point is in the heading
  intersectsHeadings(world: Point): IntersectsHeadings | undefined {
    if (!this.columnRect || !this.rowRect || !this.cornerRect) return;
    const offsets = sheets.sheet.offsets;

    if (intersects.rectanglePoint(this.cornerRect, world)) {
      return { corner: true, column: null, row: null };
    }

    // Frozen strips take precedence over the body headings scrolled beneath them.
    const frozen = this.frozenHeadingAt(world);
    if (frozen) {
      if (frozen.axis === 'column') {
        return { column: offsets.getXPlacement(world.x - frozen.shift).index, row: null };
      }
      return { row: offsets.getYPlacement(world.y - frozen.shift).index, column: null };
    }

    if (intersects.rectanglePoint(this.columnRect, world)) {
      return { column: offsets.getXPlacement(world.x).index, row: null };
    }
    if (intersects.rectanglePoint(this.rowRect, world)) {
      return { row: offsets.getYPlacement(world.y).index, column: null };
    }
  }

  // whether the point is on the heading gridLine (with tolerance)
  intersectsHeadingGridLine(
    world: Point
  ): { start: number; column: number | null; row: number | null; width?: number; height?: number } | undefined {
    if (!this.columnRect || !this.rowRect) return;

    const offsets = sheets.sheet.offsets;
    const tolerance = GRID_HEADING_RESIZE_TOLERANCE / pixiApp.viewport.scale.x;

    // Frozen strips take precedence: resize the pinned frozen column/row rather
    // than the body column/row scrolled underneath. The frozen boundaries use
    // the pinned (shifted) world position so the resize tracks the on-screen
    // strip.
    const frozen = this.frozenHeadingAt(world);
    if (frozen) {
      if (frozen.axis === 'column') {
        for (const line of this.frozenGridLinesColumns) {
          if (line.column < 1) continue;
          if (Math.abs(world.x - line.x) < tolerance) {
            return { start: line.start, column: line.column, row: null, width: line.width };
          }
        }
      } else {
        for (const line of this.frozenGridLinesRows) {
          if (line.row < 1) continue;
          if (Math.abs(world.y - line.y) < tolerance) {
            return { start: line.start, column: null, row: line.row, height: line.height };
          }
        }
      }
      return;
    }

    if (intersects.rectanglePoint(this.columnRect, world)) {
      for (const line of this.gridLinesColumns) {
        // Skip the boundary before column A (column index 0); columns are
        // 1-indexed in Quadratic, so resizing "column 0" is undefined and
        // breaks the canvas layout.
        if (line.column < 1) continue;
        if (Math.abs(world.x - line.x) < tolerance) {
          const start = offsets.getColumnPlacement(line.column);
          return { start: start.position, column: line.column, row: null, width: line.width };
        }
      }
    }

    if (intersects.rectanglePoint(this.rowRect, world)) {
      for (const line of this.gridLinesRows) {
        // Skip the boundary above row 1 (row index 0); rows are 1-indexed in
        // Quadratic, so resizing "row 0" is undefined and breaks the canvas
        // layout.
        if (line.row < 1) continue;
        if (Math.abs(world.y - line.y) < tolerance) {
          const start = offsets.getRowPlacement(line.row);
          return { start: start.position, column: null, row: line.row, height: line.height };
        }
      }
    }
  }

  /// Returns future sizes based on a new viewport position (top left)
  getFutureSizes = (viewportTopY: number): HeadingSize => {
    // When headings are off, return all zeros
    if (!gridSettings.showHeadings()) {
      return { width: 0, height: 0, unscaledWidth: 0, unscaledHeight: 0 };
    }

    const { viewport } = pixiApp;
    const bounds = viewport.getVisibleBounds();
    const viewportHeight = bounds.height;
    const screenBottom = viewportTopY + viewportHeight;
    const cellBottom = sheets.sheet.getRowFromScreen(screenBottom);
    const bottomNumberLength = cellBottom.toString().length;
    const rowWidth = this.calculateRowWidth(bottomNumberLength);

    return {
      width: rowWidth * pixiApp.viewport.scale.x,
      height: CELL_HEIGHT,
      unscaledWidth: rowWidth,
      unscaledHeight: CELL_HEIGHT / pixiApp.viewport.scale.y,
    };
  };
}
